import React, { useCallback, useEffect, useState } from 'react'
import { XMarkIcon, PlayIcon, BoltIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { getLocalApiUrl, authFetch } from '../../config/api'

function defaultTradingDay() {
  const d = new Date()
  d.setUTCDate(d.getUTCDate() - 1)
  return d.toISOString().slice(0, 10)
}

export default function RuleDryRunModal({
  open,
  ruleIds = [],
  ruleLabel = '',
  onClose,
}) {
  const [tradingDay, setTradingDay] = useState(defaultTradingDay)
  const [preparePipeline, setPreparePipeline] = useState(true)
  const [prepareRollup, setPrepareRollup] = useState(true)
  const [preflight, setPreflight] = useState(null)
  const [result, setResult] = useState(null)
  const [loadingPreflight, setLoadingPreflight] = useState(false)
  const [running, setRunning] = useState(false)
  const [runMode, setRunMode] = useState(null) // 'dry' | 'live'

  const loadPreflight = useCallback(async (day) => {
    setLoadingPreflight(true)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/rules/dry-run/preflight?trading_day=${encodeURIComponent(day)}`)
      )
      const json = await res.json()
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`)
      setPreflight(json.data || json)
    } catch (err) {
      setPreflight(null)
      toast.error(err.message || 'Preflight failed')
    } finally {
      setLoadingPreflight(false)
    }
  }, [])

  useEffect(() => {
    if (!open) {
      setPreflight(null)
      setResult(null)
      setRunMode(null)
      return
    }
    setTradingDay(defaultTradingDay())
    setResult(null)
    setRunMode(null)
  }, [open])

  useEffect(() => {
    if (!open || !tradingDay) return
    const t = setTimeout(() => loadPreflight(tradingDay), 300)
    return () => clearTimeout(t)
  }, [open, tradingDay, loadPreflight])

  const handleRun = async (persist) => {
    if (persist) {
      const n = ruleIds?.length || 0
      const ok = window.confirm(
        `Run LIVE for ${n || 'selected'} rule(s) on ${tradingDay}?\n\n` +
          'This creates Monitor Safe alerts (staging → alerts). ' +
          'Sessions that already have an open alert for the same rule are skipped.\n\n' +
          'Dry-run first if you have not reviewed hits yet.'
      )
      if (!ok) return
    }
    setRunning(true)
    setRunMode(persist ? 'live' : 'dry')
    setResult(null)
    try {
      const body = {
        trading_day: tradingDay,
        rule_ids: ruleIds?.length ? ruleIds : ['all'],
        prepare_pipeline: preparePipeline,
        prepare_rollup: prepareRollup,
        persist: !!persist,
        limit: 500,
      }
      const res = await authFetch(getLocalApiUrl('/api/rules/dry-run'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const json = await res.json()
      if (!res.ok && json.success !== true) {
        setResult({ ok: false, error: json.error || `HTTP ${res.status}`, preflight: json.preflight })
        if (json.preflight) setPreflight(json.preflight)
        throw new Error(json.error || `HTTP ${res.status}`)
      }
      setResult(json)
      setPreflight(json.preflight || preflight)
      if (json.ok) {
        if (persist) {
          const staged = json.summary?.alerts_staged ?? 0
          const skipped = json.summary?.alerts_skipped_existing ?? 0
          toast.success(
            `Live run complete — ${staged} alert(s) staged` +
              (skipped ? `, ${skipped} skipped (already open)` : '')
          )
        } else {
          toast.success('Dry-run complete')
        }
      } else {
        toast.error(json.error || 'Run could not complete')
      }
    } catch (err) {
      toast.error(err.message || 'Run failed')
    } finally {
      setRunning(false)
    }
  }

  if (!open) return null

  const blocking = preflight?.blocking || []
  const warnings = preflight?.warnings || []
  const ruleCount = ruleIds?.length || 0

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex">
      <div className="m-auto w-full max-w-3xl bg-white rounded-lg shadow-lg max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <div className="text-base font-medium">Rules run</div>
            {ruleLabel && <div className="text-sm text-gray-500 mt-0.5">{ruleLabel}</div>}
          </div>
          <button type="button" className="text-gray-600" onClick={onClose} aria-label="Close">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Trading day</label>
              <input
                type="date"
                className="input-field w-full"
                value={tradingDay}
                onChange={(e) => setTradingDay(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">Gaming day (folder delivery date − 1)</p>
            </div>
            <div className="flex items-end">
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={preparePipeline}
                    onChange={(e) => setPreparePipeline(e.target.checked)}
                  />
                  Prepare pipeline (enrich + allocate) if not ready
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={prepareRollup}
                    onChange={(e) => setPrepareRollup(e.target.checked)}
                  />
                  Prepare rollups (member stats + transaction rollup) for cumulative rules
                </label>
              </div>
            </div>
          </div>

          <div className="rounded border border-blue-100 bg-blue-50 p-3 text-xs text-blue-900">
            <strong>Performance:</strong> all selected rules ({ruleCount || 'n'}) share one session scan
            (up to 500). Cost is roughly sessions × rules of in-memory checks — not a separate
            pipeline per rule. Uncheck “Prepare pipeline” when preflight is already green to avoid
            the slow path.
          </div>

          <div className="rounded border border-gray-200 p-3 bg-gray-50 text-sm">
            <div className="font-medium text-gray-800 mb-2">Preflight</div>
            {loadingPreflight && <div className="text-gray-500">Checking data readiness…</div>}
            {!loadingPreflight && preflight && (
              <div className="space-y-2">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  <Stat label="Sessions" value={preflight.sessions?.count} ok={preflight.sessions?.ok} />
                  <Stat label="Meter" value={preflight.meter?.count} ok={preflight.meter?.ok} />
                  <Stat label="Enriched" value={preflight.enriched?.count} ok={preflight.enriched?.ok} />
                  <Stat label="Attribution" value={preflight.attribution?.count} ok={preflight.attribution?.ok} />
                  <Stat label="Txn rollup" value={preflight.rollup?.member_transaction_rollup?.count} ok={preflight.rollup?.member_transaction_rollup?.ok} />
                  <Stat label="Daily stats" value={preflight.rollup?.member_stats_daily?.count} ok={preflight.rollup?.member_stats_daily?.ok} />
                </div>
                <div className="text-gray-600">
                  Pipeline: <span className="font-mono">{preflight.pipeline_state || '—'}</span>
                  {' · '}
                  Rules ready: <span className={preflight.rules_ready ? 'text-green-700' : 'text-amber-700'}>
                    {preflight.rules_ready ? 'yes' : 'no'}
                  </span>
                </div>
                {blocking.length > 0 && (
                  <ul className="text-red-700 list-disc pl-5">
                    {blocking.map((b) => <li key={b}>{b}</li>)}
                  </ul>
                )}
                {warnings.length > 0 && (
                  <ul className="text-amber-800 list-disc pl-5">
                    {warnings.map((w) => <li key={w}>{w}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>

          {result && (
            <div className="rounded border border-gray-200 p-3 text-sm">
              <div className="font-medium mb-2">
                Results
                {result.live_run ? (
                  <span className="ml-2 text-xs font-normal text-amber-800">(live — alerts created)</span>
                ) : (
                  <span className="ml-2 text-xs font-normal text-gray-500">(dry-run — no alerts)</span>
                )}
              </div>
              {result.summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                  <div>Session rules: <strong>{result.summary.rules_run}</strong></div>
                  <div>Cumulative rules: <strong>{result.summary.cumulative_rules_run ?? 0}</strong></div>
                  <div>Sessions evaluated: <strong>{result.summary.records_evaluated}</strong></div>
                  <div>Members evaluated: <strong>{result.summary.members_triggered ?? 0}</strong> triggered</div>
                  <div className="col-span-2">Total hits: <strong>{result.summary.total_hits}</strong></div>
                  {result.live_run && (
                    <>
                      <div>Alerts staged: <strong>{result.summary.alerts_staged ?? 0}</strong></div>
                      <div>Skipped (existing): <strong>{result.summary.alerts_skipped_existing ?? 0}</strong></div>
                    </>
                  )}
                </div>
              )}
              {result.consolidation && !result.consolidation.error && (
                <p className="text-xs text-gray-600 mb-2">
                  Consolidation: created {result.consolidation.created ?? 0}, updated{' '}
                  {result.consolidation.updated ?? 0}, suppressed {result.consolidation.suppressed ?? 0}
                </p>
              )}
              {result.consolidation?.error && (
                <p className="text-xs text-amber-800 mb-2">
                  Consolidation deferred (5‑min job): {result.consolidation.error}
                </p>
              )}
              {Array.isArray(result.results) && result.results.length > 0 && (
                <div className="mb-3">
                  <div className="text-xs font-medium text-gray-600 mb-1">Session triggers</div>
                  <div className="max-h-36 overflow-y-auto border rounded">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-2 py-1 text-left">Session</th>
                          <th className="px-2 py-1 text-left">Member</th>
                          <th className="px-2 py-1 text-left">Hits</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.results.slice(0, 30).map((r) => (
                          <tr key={r.session_pk} className="border-t">
                            <td className="px-2 py-1 font-mono">{r.session_pk}</td>
                            <td className="px-2 py-1">{r.member_id || '—'}</td>
                            <td className="px-2 py-1">
                              {(r.rules || []).map((x) => x.id || x.name).join(', ') || r.error || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {Array.isArray(result.cumulative_results) && result.cumulative_results.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-gray-600 mb-1">Cumulative triggers (by member)</div>
                  <div className="max-h-36 overflow-y-auto border rounded">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-2 py-1 text-left">Member</th>
                          <th className="px-2 py-1 text-left">Rules</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.cumulative_results.slice(0, 30).map((r) => (
                          <tr key={r.member_id} className="border-t">
                            <td className="px-2 py-1">{r.member_id}</td>
                            <td className="px-2 py-1">
                              {(r.rules || []).map((x) => x.id || x.name).join(', ')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {result.ok && (!result.results?.length) && (!result.cumulative_results?.length) && (
                <p className="text-gray-600">No rule triggers for this trading day.</p>
              )}
              {!result.ok && (
                <p className="text-red-700">{result.error || 'Run did not complete'}</p>
              )}
            </div>
          )}
        </div>

        <div className="p-4 border-t flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={running}>
            Close
          </button>
          <button
            type="button"
            className="btn-secondary inline-flex items-center gap-2"
            disabled={running || !tradingDay}
            onClick={() => handleRun(false)}
          >
            <PlayIcon className="h-4 w-4" />
            {running && runMode === 'dry' ? 'Running…' : 'Run dry-run'}
          </button>
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-2"
            disabled={running || !tradingDay}
            onClick={() => handleRun(true)}
          >
            <BoltIcon className="h-4 w-4" />
            {running && runMode === 'live' ? 'Running live…' : 'Run live'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Stat({ label, value, ok }) {
  return (
    <div className={`rounded px-2 py-1 ${ok ? 'bg-green-50 text-green-900' : 'bg-gray-100 text-gray-700'}`}>
      <div className="text-xs opacity-75">{label}</div>
      <div className="font-semibold">{value ?? '—'}</div>
    </div>
  )
}
